import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import {
  Grid,
  MenuItem,
  Select,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  TextField,
  Button,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { DateRangePicker, DateRangeDelimiter } from "@material-ui/pickers";
import * as Var from "../../styles/variables";
import moment from "moment-timezone";
import {
  setPeriodType,
  setSelectedSeason,
  setSelectedSeasonLabel,
  getSeasonData,
  getMissionData,
  setSelectedMission,
  setSelectedMissionLabel,
  setFilter,
  setDateRange,
} from "../../actions";
import { isSaitamaSeasonSelected } from "../../config/SaitamaCityConfig";
import { getIsDeaAdmin } from "../../utils/deaRole";
import { isJrAccessKey } from "../../config/JrConfig";
import { getJrMissionScope } from "../../utils/helper/missionAllowList";

export const getJrPeriodControlPresentation = (isJr) => ({
  label: isJr ? "ミッション" : "期間：",
  showRadioGroup: !isJr,
});

export const getMissionAutocompleteLabel = (isJr, missionCount) =>
  isJr
    ? `登録ミッション数： ${missionCount}件`
    : `ミッションを選択する (登録 ${missionCount}件)`;

export const buildMissionRequestScope = ({
  accessKey = "",
  companyName = "",
  isDeaAdmin = false,
} = {}) => ({
  accessKey,
  companyName,
  isDeaAdmin,
  isDeaOverallMode: isDeaAdmin && accessKey === "dea",
});

export const shouldClearJrMissionSelection = ({
  accessKey = "",
  selectedMission,
  missionData,
} = {}) => {
  if (!isJrAccessKey(accessKey) || !selectedMission || selectedMission === "blank") {
    return false;
  }

  // An empty catalog is also the loading state during a mission refresh. Keep
  // the current selection until a successful catalog response is available.
  if (!Array.isArray(missionData) || missionData.length === 0) {
    return false;
  }

  const normalizedMissionId = String(selectedMission);
  const isAllowed = getJrMissionScope(accessKey, normalizedMissionId).allowed;
  const isAvailable = missionData.some(
    (mission) => String(mission?.missionId ?? "") === normalizedMissionId
  );

  return !isAllowed || !isAvailable;
};

const useStyles = makeStyles((theme) =>
  createStyles({
    calendarWrapper: {
      paddingLeft: 8,
    },
    plotCalendarWrapper: {
      paddingLeft: 0,
      position: "relative",
    },
    root: {
      backgroundColor: "#fafafa",
      zIndex: 1000,
      paddingTop: 16,
      paddingLeft: 8,
      marginTop: -12,
    },
    plotRoot: {
      backgroundColor: "transparent",
      zIndex: 1000,
      paddingTop: 0,
      paddingLeft: 0,
      marginTop: 0,
      position: "relative",
    },
    dataBtn: {
      marginTop: theme.spacing(1.2),
      marginLeft: theme.spacing(1),
      height: theme.spacing(4),
      paddingTop: theme.spacing(1.5),
      color: Var.btnLabelColor,
    },
    formControl: {
      marginTop: theme.spacing(.4),
      marginRight: theme.spacing(0),
    },
    label: {
      fontSize: 18,
      marginLeft: theme.spacing(-1),
    },
    seelctControl: {
      marginTop: theme.spacing(1),
      marginLeft: theme.spacing(-3),
    },
    missionSelect: {
      marginTop: theme.spacing(-1.5),
      minWidth: 450,
    },
    missionUnSelect: {
      marginTop: theme.spacing(-1.5),
      minWidth: 450,
    },
    missionAllSelect: {
      marginTop: theme.spacing(0),
      width: 450,
    },
    seasonUnSelect: {
      width: 450,
    },
    periodLabel: {
      marginTop: theme.spacing(2),
      marginRight: theme.spacing(3),
    },
    plotRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      gap: theme.spacing(1),
      whiteSpace: "nowrap",
    },
    plotRange: {
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      gap: theme.spacing(1),
    },
    plotContainer: {
      display: "flex",
      alignItems: "center",
      width: "auto",
      marginTop: 0,
      marginBottom: 0,
    }
  })
);

const Period = ({
  companyName,
  periodType,
  selectedSeason,
  setPeriodType,
  setSelectedSeason,
  setSelectedSeasonLabel,
  getSeasonData,
  getMissionData,
  seasonData,
  missionData,
  missionOnly,
  setSelectedMission,
  setSelectedMissionLabel,
  selectedMission,
  selectedMissionLabel,
  filterStore,
  setFilter,
  isReport,
  plotMode,
  dateRange,
  setDateRange,
  minMaxDateRange,
  onApply,
}) => {
  const classes = useStyles();
  const [error, setError] = useState([null, null]);
  const normalizeRange = (range) =>
    Array.isArray(range)
      ? range.map((value) => (value ? moment(value).toDate() : null))
      : [null, null];
  const [plotDateRange, setPlotDateRange] = useState(() => normalizeRange(dateRange));
  const isHokuden = companyName === "hokuden";
  const isMitsubishi = companyName === "mitsubishi";
  const isSaitamaPhotography =
    !isReport &&
    isSaitamaSeasonSelected(localStorage.getItem("access_key"), selectedSeason);
  const accessKey = localStorage.getItem("access_key") || "";
  const isJr = isJrAccessKey(accessKey);
  const isDeaAdmin = getIsDeaAdmin();
  const isMissionOnly = missionOnly === true;
  const shouldHidePeriodControls = isMissionOnly && !isSaitamaPhotography && !isJr;
  const periodControlPresentation = getJrPeriodControlPresentation(isJr);

  useEffect(() => {
    if (plotMode) {
      return;
    }
    if (!isMitsubishi && !isJr) {
      getSeasonData();
    }
    if (!isSaitamaPhotography) {
      getMissionData(buildMissionRequestScope({
        accessKey,
        companyName,
        isDeaAdmin,
      }));
    }
  }, [
    accessKey,
    companyName,
    getMissionData,
    isDeaAdmin,
    isMissionOnly,
    isReport,
    isSaitamaPhotography,
    isMitsubishi,
    isJr,
    plotMode,
  ]);

  useEffect(() => {
    if (plotMode || !isSaitamaPhotography) {
      return;
    }
    if (periodType !== "season") {
      setPeriodType("season");
    }
    if (selectedMission !== "blank") {
      setSelectedMission("blank");
    }
    if (selectedMissionLabel) {
      setSelectedMissionLabel("");
    }
  }, [
    plotMode,
    isSaitamaPhotography,
    periodType,
    selectedMission,
    selectedMissionLabel,
    setPeriodType,
    setSelectedMission,
    setSelectedMissionLabel,
  ]);

  useEffect(() => {
    if (plotMode) {
      return;
    }
    if (isMissionOnly && !isSaitamaPhotography) {
      setPeriodType("mission");
      setSelectedSeason("");
      setSelectedSeasonLabel("");
    }
  }, [isMissionOnly, isSaitamaPhotography, setPeriodType, setSelectedSeason, setSelectedSeasonLabel]);

  useEffect(() => {
    if (shouldClearJrMissionSelection({
      accessKey,
      selectedMission,
      missionData,
    })) {
      setSelectedMission("blank");
      setSelectedMissionLabel("");
    }
  }, [accessKey, missionData, selectedMission, setSelectedMission, setSelectedMissionLabel]);

  useEffect(() => {
    if (plotMode) {
      return;
    }
    if (seasonData.length === 0) {
      return;
    }

    const hasValidSeasonSelection =
      selectedSeason &&
      seasonData.some((season) => season.seasonId === selectedSeason);
    if (hasValidSeasonSelection) {
      return;
    }

    if (isReport && (companyName === "hokuden" || companyName === "ntt" || companyName === "tepco")) {
      setSelectedSeason(seasonData[0]["seasonId"]);
      setSelectedSeasonLabel(seasonData[0]["seasonName"]);
      return;
    }

    const today = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
    const matchedSeason = seasonData.find((season) => {
      const startDate = season.seasonStart;
      const endDate = season.seasonEnd;
      return today >= startDate && today <= endDate;
    });
    if (matchedSeason) {
      setSelectedSeason(matchedSeason.seasonId);
      setSelectedSeasonLabel(matchedSeason.seasonName);
    } else {
      setSelectedSeason(seasonData[0]["seasonId"]);
      setSelectedSeasonLabel(seasonData[0]["seasonName"]);
    }
  }, [seasonData, selectedSeason, isReport, companyName, setSelectedSeason, setSelectedSeasonLabel]);

  useEffect(() => {
    if (plotMode) {
      return;
    }
    let copyFilter = { ...filterStore };
    if (periodType === "mission") {
      copyFilter.assetType = "MISSION_ASSET";
    } else {
      copyFilter.assetType = "all";
    }
    setFilter(copyFilter);
  }, [periodType]);

  useEffect(() => {
    if (plotMode) {
      setPlotDateRange(normalizeRange(dateRange));
    }
  }, [dateRange, plotMode]);

  const handleChange = (event) => {
    setPeriodType(event.target.value);
    if (event.target.value === "season") {
      setSelectedMission("");
      setSelectedMissionLabel("");
    } else {
      setSelectedSeason("");
      setSelectedSeasonLabel("");
    }
  };

  const handleSelecSeasonChange = (event) => {
    setSelectedSeason(event.target.value);
    setSelectedSeasonLabel(
      seasonData.find((season) => season.seasonId === event.target.value)
        ?.seasonName || ""
    );
  };

  const handleSelectMissionChange = (event) => {
    if (!event || typeof event === "string") {
      setSelectedMission("blank");
      setSelectedMissionLabel("");
      return;
    }
    setSelectedMission(event.missionId.toString());
    setSelectedMissionLabel(event.missionName);
  };

  const isStarted = (startTime, currentTime) => {
    return startTime.isBefore(currentTime);
  };

  if (plotMode) {
    return (
      <div className={classes.plotCalendarWrapper}>
        <div className={classes.plotRoot}>
          <Grid container spacing={1} direction="row" alignItems="center" wrap="nowrap">
            <Grid item className={classes.plotContainer}>
              <DateRangePicker
                startText="開始"
                endText="終了"
                disableHighlightToday={true}
                showToolbar={false}
                allowSameDateSelection={true}
                PopperProps={{
                  style: {
                    zIndex: 3000,
                  },
                }}
                DialogProps={{
                  PaperProps: {
                    style: {
                      zIndex: 3000,
                    },
                  },
                }}
                minDate={minMaxDateRange[0] !== null ? minMaxDateRange[0] : null}
                maxDate={minMaxDateRange[1] !== null ? minMaxDateRange[1] : null}
                value={plotDateRange}
                onChange={(newValue) => {
                  setPlotDateRange(newValue);
                }}
                renderInput={(startProps, endProps) => (
                  <div className={classes.plotRange}>
                    <TextField
                      {...startProps}
                      error={Boolean(error[0])}
                      size="small"
                      helperText=""
                      style={{ width: 132 }}
                    />
                    <DateRangeDelimiter>
                      <span className={classes.delimiter}>~</span>
                    </DateRangeDelimiter>
                    <TextField
                      {...endProps}
                      error={Boolean(error[0])}
                      size="small"
                      helperText=""
                      style={{ width: 132 }}
                    />
                  </div>
                )}
              />
            </Grid>
            <Grid item>
              <Button
                className={classes.dataBtn}
                variant="contained"
                color="primary"
                onClick={() => {
                  if (typeof onApply === "function") {
                    onApply(plotDateRange);
                  }
                }}
              >
                表示
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.calendarWrapper}>
      <div className={classes.root}>
        <Grid container spacing={1} direction="row">
          {!shouldHidePeriodControls && (
            <>
              <Grid item>
                <div className={classes.periodLabel}>{periodControlPresentation.label}</div>
              </Grid>
              {!isReport && !isSaitamaPhotography && periodControlPresentation.showRadioGroup && (
                <Grid item>
                  <FormControl component="fieldset" className={classes.formControl}>
                    <RadioGroup
                      name="period"
                      className={classes.group}
                      value={periodType}
                      onChange={handleChange}
                      row
                    >
                      <FormControlLabel
                        className={classes.label}
                        value="season"
                        control={<Radio />}
                        label="シーズン"
                      />
                      <FormControlLabel
                        className={classes.label}
                        value="mission"
                        control={<Radio />}
                        label="ミッション"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}
            </>
          )}
          <Grid item>
            <FormControl className={classes.seelctControl}>
              {isSaitamaPhotography || (periodType === "season" && !isMissionOnly) ? (
                <Select
                  labelId="season-select-label"
                  value={selectedSeason}
                  onChange={handleSelecSeasonChange}
                  displayEmpty
                  label="シーズンを選択"
                  className={
                    selectedSeason === "blank" || !selectedSeason
                      ? classes.seasonUnSelect
                      : undefined
                  }
                >
                  <MenuItem value="blank" disabled>
                    シーズンを選択する
                  </MenuItem>
                  {seasonData.map((s, index) => (
                    <MenuItem key={index} value={s.seasonId}>
                      {s.seasonAbbr} : {s.seasonStart} ~ {s.seasonEnd}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                // mission dropdown
                <Autocomplete
                  id="mission-select-autocomplete"
                  options={missionData}
                  getOptionLabel={(option) => option.missionName || ""}
                  getOptionSelected={(option, value) =>
                    option.missionId.toString() === value.toString()
                  }
                  value={
                    missionData.find(
                      (mission) =>
                        mission.missionId.toString() === selectedMission
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    if (newValue) {
                      handleSelectMissionChange(newValue);
                    } else {
                      handleSelectMissionChange("blank");
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={getMissionAutocompleteLabel(isJr, missionData.length)}
                      className={
                        selectedMission === "blank"
                          ? classes.missionUnSelect
                          : selectedMission === "all"
                          ? classes.missionAllSelect
                          : classes.missionSelect
                      }
                    />
                  )}
                  renderOption={(option) => (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "450px",
                      }}
                    >
                      <Typography variant="subtitle1">
                        {option.missionName}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        style={{ marginLeft: 14 }}
                      >
                        {moment(option.missionStart).format("YYYY-MM-DD HH:mm")}{" "}
                        ~ {moment(option.missionEnd).format("YYYY-MM-DD HH:mm")}
                      </Typography>
                    </div>
                  )}
                  getOptionDisabled={(option) => {
                    const startTime = moment(
                      option.missionStart,
                      "YYYY-MM-DD HH:mm"
                    );
                    const currentTime = moment()
                      .tz("Asia/Tokyo")
                      .format("YYYY-MM-DD HH:mm");
                    return (
                      option.missionId.toString() === "blank" ||
                      !isStarted(startTime, currentTime)
                    );
                  }}
                  className={classes.missionAllSelect}
                />
              )}
            </FormControl>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

Period.prototypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  dateRange: state.app.dateRange,
  periodType: state.app.periodType,
  seasonData: state.app.seasonData,
  missionData: state.app.missionData,
  missionOnly: state.app.missionOnly,
  selectedSeason: state.app.selectedSeason,
  selectedMission: state.app.selectedMission,
  selectedMissionLabel: state.app.selectedMissionLabel,
  filterStore: state.home.filter,
  dateRange: state.app.dateRange,
  minMaxDateRange: state.app.minMaxDateRange,
});

const mapDispatchToProps = (dispatch) => ({
  setPeriodType: (param) => dispatch(setPeriodType(param)),
  setSelectedSeason: (param) => dispatch(setSelectedSeason(param)),
  setSelectedSeasonLabel: (param) => dispatch(setSelectedSeasonLabel(param)),
  getSeasonData: () => dispatch(getSeasonData()),
  getMissionData: (param) => dispatch(getMissionData(param)),
  setSelectedMission: (param) => dispatch(setSelectedMission(param)),
  setSelectedMissionLabel: (param) => dispatch(setSelectedMissionLabel(param)),
  setFilter: (param) => dispatch(setFilter(param)),
  setDateRange: (param) => dispatch(setDateRange(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(Period));
